import React from "react"
import Chart from "react-apexcharts"

const CHART_COLORS = [
  "#A78BFA",
  "#34D399",
  "#FB923C",
  "#F87171",
  "#FCD34D",
  "#2DD4BF",
  "#F472B6",
  "#60A5FA",
]

const BarChart = ({
  title,
  categories,
  series,
  horizontal = false,
  pound,
  colorIndex = 0,
  onBarClick,
}) => {
  const color = CHART_COLORS[colorIndex % CHART_COLORS.length]

  const getFormatter = val => {
    if (!val && val !== 0) return "N/A"
    const rounded = Math.round(Number(val))
    return pound
      ? `£${rounded.toLocaleString("en-GB")}`
      : rounded.toLocaleString("en-GB")
  }

  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      background: "transparent",
      events: onBarClick
        ? {
            dataPointSelection: (_e, _ctx, config) => {
              const idx = config.dataPointIndex
              if (idx < 0) return
              onBarClick(categories[idx])
            },
          }
        : {},
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: { enabled: true, delay: 100 },
      },
      dropShadow: {
        enabled: true,
        color,
        top: 4,
        blur: 10,
        opacity: 0.12,
      },
    },

    plotOptions: {
      bar: {
        horizontal,
        barHeight: "55%",
        columnWidth: "45%",
        borderRadius: 5,
        borderRadiusApplication: "end",
        distributed: true,
        dataLabels: { position: horizontal ? "center" : "top" },
      },
    },

    stroke: {
      width: 0,
    },

    dataLabels: {
      enabled: horizontal,
      formatter: val => getFormatter(val),
      style: {
        fontSize: "11px",
        fontWeight: 700,
        colors: ["#fff"],
      },
    },

    fill: {
      type: "solid",
      opacity: 1,
    },

    colors: CHART_COLORS,

    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: -90,
        style: {
          colors: "#94a3b8",
          fontSize: "12px",
          fontWeight: 500,
        },
        formatter: val => (typeof val === "number" ? getFormatter(val) : val),
      },
    },

    yaxis: {
      labels: {
        maxWidth: 160,
        style: { colors: "#64748b", fontSize: "11px", fontWeight: 600 },
        formatter: val => (typeof val === "number" ? getFormatter(val) : val),
      },
    },

    legend: { show: false },

    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: horizontal } },
      yaxis: { lines: { show: !horizontal } },
      padding: { top: 0, right: 16, bottom: 0, left: horizontal ? 20 : 8 },
    },

    tooltip: {
      theme: "dark",
      style: { fontSize: "12px" },
      x: { formatter: val => val },
      y: { formatter: val => getFormatter(val) },
    },

    states: {
      hover: { filter: { type: "lighten", value: 0.08 } },
      active: { filter: { type: "darken", value: 0.1 } },
    },
  }

  return (
    <div
      style={{
        background: "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
        borderRadius: "16px",
        border: `1px solid ${color}22`,
        boxShadow: `0 4px 24px ${color}14, 0 1px 4px rgba(0,0,0,0.04)`,
        position: "relative",
        overflow: "hidden",
        marginBottom: "1.5rem",
      }}
    >
      {/* Top accent bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${color}, ${color}88)`,
          borderRadius: "16px 16px 0 0",
        }}
      />

      <div style={{ padding: "20px 16px 8px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "4px",
            padding: "0 8px",
          }}
        >
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: color,
              boxShadow: `0 0 8px ${color}88`,
            }}
          />
          <h4
            style={{
              margin: 0,
              fontSize: "15px",
              fontWeight: 700,
              color: "#1e293b",
            }}
          >
            {title}
          </h4>
        </div>

        <Chart options={options} series={series} type="bar" height={320} />
      </div>
    </div>
  )
}

export default BarChart
